import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ActionIcon, Anchor, Box, Group, Paper, Skeleton, Stack, Text } from '@mantine/core';
import { useOpenLightbox } from './Lightbox';
import { IconChevronLeft, IconChevronRight, IconFile, IconPhotoOff } from '@tabler/icons-react';

/**
 * Shared, channel-agnostic chat-bubble content components, used by every
 * inbox's message renderer (Messenger registry + the Instagram inbox today,
 * WhatsApp + the builder live-preview next). They take normalized presentation
 * props — each channel's renderer parses its own payload and maps onto these —
 * so improving a bubble here improves it everywhere (and the future builder
 * preview). See docs/chat-revamp/04-bubble-ui-plan.md.
 */

export type MediaKind = 'image' | 'video' | 'audio' | 'file';

export interface BubbleButton {
  title: string;
  /** Present only for web_url buttons; postback/phone render as display-only. */
  url?: string | null;
}

export interface BubbleCard {
  title?: string | null;
  subtitle?: string | null;
  imageUrl?: string | null;
  buttons?: BubbleButton[];
}

/** Plain message text — preserves newlines, wraps long words. */
export function BubbleText({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </Text>
  );
}

/** Friendly text for an inbound message we can't render natively, so the inbox
 *  never shows a raw [type] or an empty bubble. */
export function unsupportedNote(app: string): string {
  return `Sent a message — open in ${app} to view`;
}

/** Dimmed soft-note bubble for an unrenderable inbound type — the universal
 *  safety net shared by every channel's renderer. */
export function SoftNote({ app }: { app: string }) {
  return (
    <Group gap={6} wrap="nowrap" c="dimmed">
      <IconFile size={14} />
      <Text size="xs">{unsupportedNote(app)}</Text>
    </Group>
  );
}

function MediaUnavailable({ label }: { label: string }) {
  return (
    <Group gap={6} wrap="nowrap" c="dimmed">
      <IconPhotoOff size={16} />
      <Text size="xs">{label} unavailable</Text>
    </Group>
  );
}

/** Robust media — fixed-size skeleton while loading (no layout shift), an
 *  onError fallback chip when the URL has expired/404'd, and click-to-zoom for
 *  images. Fixes the "image sometimes doesn't show" + jumpiness defects. */
export function MediaBubble({
  kind,
  url,
  alt = 'attachment',
  fileName,
}: {
  kind: MediaKind;
  url: string | null | undefined;
  alt?: string;
  /** Original filename, when the channel gives us one — a received PDF should
   *  read "invoice.pdf", not a generic "View attachment". */
  fileName?: string | null;
}) {
  if (!url) return <MediaUnavailable label={kind} />;
  if (kind === 'image') return <ImageMedia url={url} alt={alt} />;
  if (kind === 'video') return <VideoMedia url={url} />;
  if (kind === 'audio')
    return <audio src={url} controls style={{ maxWidth: 240, width: '100%' }} />;
  return <FileMedia url={url} fileName={fileName} />;
}

function ImageMedia({ url, alt }: { url: string; alt: string }) {
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const openLightbox = useOpenLightbox();
  if (state === 'error') return <MediaUnavailable label="Image" />;
  return (
    <Box style={{ position: 'relative', maxWidth: 260, minWidth: 140 }}>
      {state === 'loading' && <Skeleton height={160} radius="sm" />}
      <img
        src={url}
        alt={alt}
        data-lb-src={url}
        onLoad={() => setState('loaded')}
        onError={() => setState('error')}
        onClick={() => openLightbox(url)}
        style={{
          display: state === 'loaded' ? 'block' : 'none',
          maxWidth: '100%',
          maxHeight: 320,
          borderRadius: 8,
          cursor: 'pointer',
        }}
      />
    </Box>
  );
}

function VideoMedia({ url }: { url: string }) {
  const [error, setError] = useState(false);
  if (error) return <MediaUnavailable label="Video" />;
  return (
    <video
      src={url}
      controls
      onError={() => setError(true)}
      style={{ maxWidth: 280, width: '100%', borderRadius: 8 }}
    />
  );
}

function FileMedia({ url, fileName }: { url: string; fileName?: string | null }) {
  return (
    <Group
      gap={8}
      wrap="nowrap"
      px="xs"
      py={6}
      style={{
        border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        borderRadius: 8,
        maxWidth: 240,
      }}
    >
      <IconFile size={18} />
      <Anchor
        href={url}
        target="_blank"
        rel="noreferrer"
        size="sm"
        truncate
        title={fileName ?? undefined}
      >
        {fileName || 'View attachment'}
      </Anchor>
    </Group>
  );
}

/** Display-only "button" — looks like the Messenger/IG button the recipient
 *  sees; web_url buttons are real links, postback/phone are inert. */
function PseudoButton({ title, url }: BubbleButton) {
  const inner = (
    <Text size="xs" fw={500} ta="center" c="blue.6" truncate>
      {title}
    </Text>
  );
  const style: CSSProperties = {
    display: 'block',
    border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
    borderRadius: 6,
    padding: '6px 8px',
    background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
  };
  return url ? (
    <Anchor href={url} target="_blank" rel="noreferrer" underline="never" style={style}>
      {inner}
    </Anchor>
  ) : (
    <Box style={style}>{inner}</Box>
  );
}

export function TemplateButtons({ buttons }: { buttons: BubbleButton[] }) {
  if (!buttons.length) return null;
  return (
    <Box mt={6} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {buttons.map((b, i) => (
        <PseudoButton key={i} title={b.title} url={b.url} />
      ))}
    </Box>
  );
}

const CARD_W = 240;
const CARD_GAP = 8;

function CardImage({ url }: { url: string }) {
  const [error, setError] = useState(false);
  if (error) return null;
  return (
    <img
      src={url}
      alt=""
      onError={() => setError(true)}
      style={{
        width: '100%',
        height: 130,
        objectFit: 'cover',
        display: 'block',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
      }}
    />
  );
}

function CardItem({ card }: { card: BubbleCard }) {
  return (
    <Paper
      withBorder
      radius="sm"
      style={{
        overflow: 'hidden',
        // Own bg + text so the card stays readable inside a tinted outbound bubble.
        background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-5))',
        color: 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))',
      }}
    >
      {card.imageUrl && <CardImage url={card.imageUrl} />}
      <Box p="xs">
        {card.title && (
          <Text size="sm" fw={600} lineClamp={2}>
            {card.title}
          </Text>
        )}
        {card.subtitle && (
          <Text size="xs" c="dimmed" lineClamp={3}>
            {card.subtitle}
          </Text>
        )}
        {card.buttons && card.buttons.length > 0 && <TemplateButtons buttons={card.buttons} />}
      </Box>
    </Paper>
  );
}

/** Generic/carousel template. A single card renders inline; multiple cards
 *  become a smooth scroll-snap carousel with peek of the next card, dot
 *  indicators, and (desktop) hover arrows. Replaces the old non-interactive
 *  "▸ title" list and finally shows the card images. */
export function TemplateCards({ cards }: { cards: BubbleCard[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  if (cards.length === 0) return null;
  if (cards.length === 1) {
    return (
      <Box style={{ width: CARD_W, maxWidth: '100%' }}>
        <CardItem card={cards[0]!} />
      </Box>
    );
  }

  function scrollToCard(i: number) {
    ref.current?.scrollTo({ left: i * (CARD_W + CARD_GAP), behavior: 'smooth' });
  }
  function onScroll() {
    const el = ref.current;
    if (el) setActive(Math.round(el.scrollLeft / (CARD_W + CARD_GAP)));
  }

  return (
    <Box style={{ position: 'relative', maxWidth: '100%' }}>
      <Box
        ref={ref}
        onScroll={onScroll}
        style={{
          display: 'flex',
          gap: CARD_GAP,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingBottom: 4,
          scrollbarWidth: 'thin',
        }}
      >
        {cards.map((c, i) => (
          <Box key={i} style={{ flex: `0 0 ${CARD_W}px`, scrollSnapAlign: 'start' }}>
            <CardItem card={c} />
          </Box>
        ))}
      </Box>
      <ActionIcon
        variant="default"
        radius="xl"
        size="sm"
        visibleFrom="sm"
        aria-label="Previous card"
        onClick={() => scrollToCard(Math.max(0, active - 1))}
        style={{ position: 'absolute', left: -6, top: 60 }}
      >
        <IconChevronLeft size={14} />
      </ActionIcon>
      <ActionIcon
        variant="default"
        radius="xl"
        size="sm"
        visibleFrom="sm"
        aria-label="Next card"
        onClick={() => scrollToCard(Math.min(cards.length - 1, active + 1))}
        style={{ position: 'absolute', right: -6, top: 60 }}
      >
        <IconChevronRight size={14} />
      </ActionIcon>
      <Group justify="center" gap={5} mt={4}>
        {cards.map((_, i) => (
          <Box
            key={i}
            onClick={() => scrollToCard(i)}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              cursor: 'pointer',
              background:
                i === active
                  ? 'var(--mantine-color-blue-6)'
                  : 'light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-3))',
            }}
          />
        ))}
      </Group>
    </Box>
  );
}

/** Quick-reply chips — pill-shaped, like the recipient's tappable options. */
export function QuickReplyChips({ text, chips }: { text?: string | null; chips: string[] }) {
  return (
    <Stack gap={6}>
      {text && <BubbleText>{text}</BubbleText>}
      {chips.length > 0 && (
        <Group gap={6}>
          {chips.map((c, i) => (
            <Box
              key={i}
              style={{
                // White pill so chips stay legible on a blue outbound bubble
                // and on a light incoming bubble alike.
                background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
                color: 'var(--mantine-color-blue-6)',
                borderRadius: 999,
                padding: '2px 10px',
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {c}
            </Box>
          ))}
        </Group>
      )}
    </Stack>
  );
}

/** Rich attachment card for shared posts/reels/stories/products — an optional
 *  preview image, a label, and a link when available. */
export function AttachmentCard({
  label,
  url,
  imageUrl,
}: {
  label: string;
  url?: string | null;
  imageUrl?: string | null;
}) {
  return (
    <Paper
      withBorder
      radius="sm"
      style={{
        overflow: 'hidden',
        maxWidth: CARD_W,
        background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-5))',
        color: 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))',
      }}
    >
      {imageUrl && <CardImage url={imageUrl} />}
      <Box p="xs">
        <Text size="xs" fw={600}>
          {label}
        </Text>
        {url && (
          <Anchor href={url} target="_blank" rel="noreferrer" size="xs">
            View
          </Anchor>
        )}
      </Box>
    </Paper>
  );
}

export interface ReceiptLine {
  title?: string | null;
  subtitle?: string | null;
  quantity?: number | null;
  price?: number | null;
}
export interface ReceiptView {
  recipientName?: string | null;
  orderNumber?: string | null;
  currency?: string | null;
  paymentMethod?: string | null;
  summary?: { totalCost?: number | null } | null;
  elements?: ReceiptLine[];
}

/** Format a money amount without a currency-formatting dependency: 2dp + the
 *  ISO code as a suffix (e.g. "12.50 USD"). */
function money(amount: number | null | undefined, currency?: string | null): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  return `${amount.toFixed(2)}${currency ? ` ${currency}` : ''}`;
}

/** Receipt template bubble — order header, line items, and a total. */
export function ReceiptCard({ receipt }: { receipt: ReceiptView }) {
  const cur = receipt.currency;
  const items = receipt.elements ?? [];
  return (
    <Paper
      withBorder
      radius="sm"
      style={{
        overflow: 'hidden',
        maxWidth: CARD_W,
        background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-5))',
        color: 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))',
      }}
    >
      <Box p="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          Receipt
        </Text>
        {receipt.orderNumber && (
          <Text size="sm" fw={600}>
            Order {receipt.orderNumber}
          </Text>
        )}
        {receipt.recipientName && (
          <Text size="xs" c="dimmed">
            {receipt.recipientName}
          </Text>
        )}
        {items.length > 0 && (
          <Stack gap={2} mt={6}>
            {items.map((it, i) => (
              <Group key={i} justify="space-between" wrap="nowrap" gap="xs">
                <Text size="xs" lineClamp={1}>
                  {it.title || 'Item'}
                  {it.quantity ? ` × ${it.quantity}` : ''}
                </Text>
                <Text size="xs">{money(it.price, cur)}</Text>
              </Group>
            ))}
          </Stack>
        )}
        {receipt.summary?.totalCost != null && (
          <Group
            justify="space-between"
            mt={6}
            pt={4}
            style={{
              borderTop:
                '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            }}
          >
            <Text size="xs" fw={700}>
              Total
            </Text>
            <Text size="xs" fw={700}>
              {money(receipt.summary.totalCost, cur)}
            </Text>
          </Group>
        )}
        {receipt.paymentMethod && (
          <Text size="xs" c="dimmed" mt={2}>
            Paid · {receipt.paymentMethod}
          </Text>
        )}
      </Box>
    </Paper>
  );
}
